if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      try {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.modalContent) this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      } finally {
        this.releaseQuickAddLock();
      }

      window.setTimeout(() => {
        this.resumeAutoplayOnPage();
      }, 50);
    }

    async show(opener) {
      if (
        this.isLoading ||
        this.hasAttribute('open') ||
        this.constructor.activeInstance
      ) return;

      const rawProductUrl = opener.getAttribute('data-product-url');
      let productUrl;

      try {
        if (!rawProductUrl) throw new Error('Missing product URL');

        productUrl = new URL(rawProductUrl, window.location.origin);
        const isSafeUrl =
          productUrl.origin === window.location.origin &&
          ['http:', 'https:'].includes(productUrl.protocol);

        if (!isSafeUrl) throw new Error('Invalid product URL');
      } catch (error) {
        console.warn('[QuickAdd] Invalid product URL.', error);
        return;
      }

      this.constructor.activeInstance = this;
      this.isLoading = true;
      const spinner = opener.querySelector('.loading-overlay-spinner');
      const wasDisabled = opener.disabled;
      const previousAriaDisabled = opener.getAttribute('aria-disabled');

      opener.disabled = true;
      opener.setAttribute('aria-disabled', 'true');
      opener.classList.add('loading');
      spinner?.classList.remove('hidden');

      let timeoutId;
      let failure;

      try {
        const controller = new AbortController();
        timeoutId = window.setTimeout(() => controller.abort(), 10000);

        const response = await fetch(productUrl.href, { signal: controller.signal });
        if (!response.ok) throw new Error(`Product request failed with HTTP ${ response.status }`);

        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

        if (!productElement || !productElement.dataset.section) {
          throw new Error('MainProduct section is unavailable');
        }

        this.productElement = productElement;
        this.preventDuplicatedIDs();
        this.removeDOMElements();
        this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

        removeElementsByScreenSize(this.modalContent);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.removeGalleryListSemantic();
        this.updateImageSizes();
        this.preventVariantURLSwitching();
      } catch (error) {
        failure = error;
      } finally {
        window.clearTimeout(timeoutId);
        this.isLoading = false;
        opener.disabled = wasDisabled;

        if (previousAriaDisabled === null) {
          opener.removeAttribute('aria-disabled');
        } else {
          opener.setAttribute('aria-disabled', previousAriaDisabled);
        }

        opener.classList.remove('loading');
        spinner?.classList.add('hidden');
      }

      if (failure) {
        this.releaseQuickAddLock();
        if (this.modalContent) this.modalContent.innerHTML = '';
        console.warn('[QuickAdd] Falling back to the product page.', failure);
        window.location.assign(productUrl.href);
        return;
      }

      const bodyHadOverflowHiddenClass = document.body.classList.contains('overflow-hidden');

      try {
        super.show(opener);
        this.autoplayActiveMediaInModal();

        // Add smooth animation here
        this.modalContent.style.opacity = '0';
        this.modalContent.style.transform = 'scale(0.8)';
        this.modalContent.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

        window.setTimeout(() => {
          this.modalContent.style.opacity = '1';
          this.modalContent.style.transform = 'scale(1)';
        }, 0);
      } catch (error) {
        removeTrapFocus();
        this.removeAttribute('open');
        if (!bodyHadOverflowHiddenClass) document.body.classList.remove('overflow-hidden');
        this.releaseQuickAddLock();
        if (this.modalContent) this.modalContent.innerHTML = '';
        console.warn('[QuickAdd] Could not open the modal; falling back to the product page.', error);
        window.location.assign(productUrl.href);
      }
    }

    releaseQuickAddLock() {
      if (this.constructor.activeInstance === this) {
        this.constructor.activeInstance = null;
      }
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      if (!product) return;

      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }

    autoplayActiveMediaInModal() {
      const activeSlide =
        this.modalContent.querySelector('.product-media-item.is-active') ||
        this.modalContent.querySelector('[data-media-id].is-active') ||
        this.modalContent.querySelector('[data-media-id]');

      if (!activeSlide) return;

      const deferredMedia = activeSlide.querySelector('deferred-media[data-autoplay="true"]');
      if (!deferredMedia) return;

      deferredMedia.loadContent(false);
      if (typeof deferredMedia.autoplayVideo === 'function') {
        deferredMedia.autoplayVideo();
      } else {
        const video = deferredMedia.querySelector('video');
        if (video) {
          video.muted = true;
          video.playsInline = true;
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        }
      }
    }

    resumeAutoplayOnPage() {
      const galleries = document.querySelectorAll('media-gallery');

      galleries.forEach((gallery) => {
        if (gallery.closest('quick-add-modal, .quick-add-modal, #QuickAddModal')) return;

        const activeSlide =
          gallery.querySelector('.product-media-item.is-active') ||
          gallery.querySelector('[data-media-id].is-active') ||
          gallery.querySelector('[data-media-id]');

        if (!activeSlide) return;

        const deferredMedia = activeSlide.querySelector('deferred-media[data-autoplay="true"]');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);

        if (typeof deferredMedia.autoplayVideo === 'function') {
          deferredMedia.autoplayVideo();
        } else {
          const video = deferredMedia.querySelector('video');
          if (video) {
            video.muted = true;
            video.playsInline = true;
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          }
        }
      });
    }

  });
}
